/**
 * Renders the filtered swatch grid from catalog records, in chunks, as the user scrolls.
 *
 * The server renders the .swatch-filtered-group containers (16 of them - headings, count, and an
 * empty .swatch-filtered-grid) and this fills them. Containers stay server-rendered because there
 * are only sixteen and they carry the category titles; it is the ~2,100 cards inside them that
 * were worth not sending.
 *
 * Chunking exists for the worst case rather than the common one. A typical filter matches ~120
 * swatches, which would be fine rendered in one go, but nothing stops a visitor selecting every
 * colour range and matching most of the catalog - and building 2,000 cards in a single task would
 * be slower than the server-rendered HTML this replaces.
 */
import { renderSwatchCard } from './swatch-card-render';

const CHUNK_SIZE = 120;

// Far enough ahead that the next chunk is usually rendered before it is scrolled into view.
const SENTINEL_MARGIN = '1200px';

const createSwatchGridRenderer = (options) => {
  const {
    filteredMode,
    filteredCards,
    favorites,
    onChunkRendered,
    appendVisualizeTile,
  } = options;

  const groups = filteredMode
    ? Array.prototype.slice.call(filteredMode.querySelectorAll('.swatch-filtered-group'))
    : [];

  const grids = groups.map(group => group.querySelector('.swatch-filtered-grid'));

  // One queue entry per group that has any matches, in group order.
  let queue = [];
  let queueIndex = 0;
  let sentinel = null;
  let observer = null;

  const assetFor = id => (
    Object.prototype.hasOwnProperty.call(favorites, id) ? favorites[id] : null
  );

  // renderChunk and placeSentinel call each other: a chunk ends by placing the sentinel that
  // triggers the next one. Declared here so neither has to be defined before the other.
  let renderChunk = null;

  const teardownObserver = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    if (sentinel && sentinel.parentNode) {
      sentinel.parentNode.removeChild(sentinel);
    }
    sentinel = null;
  };

  const placeSentinel = () => {
    teardownObserver();

    const entry = queue[queueIndex];
    if (!entry) {
      return;
    }

    sentinel = document.createElement('div');
    sentinel.className = 'swatch-render-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    entry.grid.parentNode.insertBefore(sentinel, entry.grid.nextSibling);

    if (typeof window.IntersectionObserver !== 'function') {
      // No observer: render the rest now rather than leave the grid truncated. Correctness over
      // the scroll-time saving, since the alternative is silently missing swatches.
      while (queueIndex < queue.length) {
        renderChunk();
      }
      return;
    }

    observer = new window.IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        renderChunk();
      }
    }, { rootMargin: SENTINEL_MARGIN });

    observer.observe(sentinel);
  };

  renderChunk = () => {
    let budget = CHUNK_SIZE;
    const added = [];

    while (budget > 0 && queueIndex < queue.length) {
      const entry = queue[queueIndex];
      const take = entry.records.slice(entry.rendered, entry.rendered + budget);

      if (take.length > 0) {
        const html = take.map(r => renderSwatchCard(r, assetFor(r.id))).join('');

        // One innerHTML-equivalent write per group per chunk. insertAdjacentHTML rather than
        // innerHTML += so already-rendered cards in this group are not reparsed, which would
        // drop the cart state just re-applied to them.
        entry.grid.insertAdjacentHTML('beforeend', html);

        // The cards just inserted are the last take.length children, so they can be collected
        // without rescanning the grid.
        const { children } = entry.grid;
        for (let i = children.length - take.length; i < children.length; i += 1) {
          added.push(children[i]);
          filteredCards.push(children[i]);
        }

        entry.rendered += take.length;
        budget -= take.length;
      }

      if (entry.rendered >= entry.records.length) {
        // The visualize tile goes after the last card, so it can only be placed once the group
        // is fully rendered.
        if (typeof appendVisualizeTile === 'function') {
          appendVisualizeTile(entry.grid, entry.records.length, entry.group);
        }
        queueIndex += 1;
      }
    }

    if (typeof onChunkRendered === 'function') {
      onChunkRendered(added);
    }

    if (queueIndex >= queue.length) {
      teardownObserver();
      return;
    }

    placeSentinel();
  };

  /**
   * Replaces the grid contents with the given records.
   *
   * @param recordsByGroup array indexed by group, each holding that group's matching records in
   *                       catalog order
   */
  const render = (recordsByGroup) => {
    teardownObserver();

    // filteredCards is captured by reference in swatch-filters.js and swatch-detail.js, so it has
    // to be emptied in place - reassigning it here would leave those modules holding the old array.
    filteredCards.length = 0;

    grids.forEach((grid) => {
      if (grid) {
        grid.innerHTML = '';
      }
    });

    queue = [];
    queueIndex = 0;

    groups.forEach((group, index) => {
      const records = recordsByGroup[index] || [];
      if (records.length === 0 || !grids[index]) {
        return;
      }
      queue.push({
        index, group, grid: grids[index], records, rendered: 0,
      });
    });

    if (queue.length > 0) {
      renderChunk();
    }
  };

  /**
   * Renders chunks until the given swatch has a card, and returns it.
   *
   * Used by deep links. A `?detail=` or gallery link can point at a swatch anywhere in the
   * result set, and waiting for the visitor to scroll to it is not an option when the whole
   * point of the link is to open it.
   */
  const ensureCardRendered = (swatchId) => {
    const selector = `.swatch-card-col[data-swatch-id="${swatchId}"]`;
    const existing = filteredMode && filteredMode.querySelector(selector);
    if (existing) {
      return existing;
    }

    // Only worth rendering ahead if the swatch is actually in the current result set.
    const wanted = String(swatchId);
    const inQueue = queue.some(entry => entry.records.some(r => r.id === wanted));
    if (!inQueue) {
      return null;
    }

    while (queueIndex < queue.length) {
      renderChunk();
      const found = filteredMode.querySelector(selector);
      if (found) {
        return found;
      }
    }

    return filteredMode.querySelector(selector);
  };

  /**
   * Renders one more chunk on demand. Returns how many cards it added, so a caller can tell
   * "there is more" from "that was everything".
   *
   * Exists for the detail panel's next button: stepping past the last rendered card should pull
   * the next cards in the same way scrolling does, rather than behaving as if the result set
   * ended there.
   */
  const renderMoreCards = () => {
    if (queueIndex >= queue.length) {
      return 0;
    }
    const before = filteredCards.length;
    renderChunk();
    return filteredCards.length - before;
  };

  /**
   * Renders everything still queued.
   *
   * Only for the case that genuinely needs the whole set present: stepping backwards off the
   * first swatch, which wraps to the last one and so has to know what the last one is.
   */
  const renderAllRemaining = () => {
    const before = filteredCards.length;
    while (queueIndex < queue.length) {
      renderChunk();
    }
    return filteredCards.length - before;
  };

  /**
   * Re-appends the visualize tile to every group that is already fully rendered.
   *
   * Tile placement depends on viewport width, so it has to be redone on resize - but resizing
   * must not throw away rendered cards. Before the grid was built client-side, re-running the
   * whole filtered view on resize was free; now it would reset the visitor to the first chunk
   * and move the page under them.
   */
  const refreshVisualizeTiles = () => {
    if (typeof appendVisualizeTile !== 'function') {
      return;
    }
    queue.forEach((entry) => {
      if (entry.rendered >= entry.records.length) {
        appendVisualizeTile(entry.grid, entry.records.length, entry.group);
      }
    });
  };

  return {
    render,
    refreshVisualizeTiles,
    ensureCardRendered,
    renderMoreCards,
    renderAllRemaining,
    hasMoreToRender: () => queueIndex < queue.length,
    groupCount: groups.length,
    destroy: teardownObserver,
  };
};

export default createSwatchGridRenderer;
export { createSwatchGridRenderer, CHUNK_SIZE };
